import { LabelsCategories } from '@/models/labels';

import { PendingLabel } from '../types';
import { formatBadge } from './text';

export type CategoryLookup = {
  findCategoryName: (name: string) => { id: number; name: string } | undefined;
  findLabelName: (name: string) => PendingLabel | undefined;
  isLabelLinkedCategory: (categoryId: number, labelId: number) => boolean;
};

export const buildCategoryLabelOption = (
  { categoryName, labelName }: { categoryName: string; labelName: string },
  { findCategoryName, findLabelName, isLabelLinkedCategory }: CategoryLookup
): PendingLabel => {
  const existingCategory = findCategoryName(categoryName);
  const existingLabel = findLabelName(labelName);
  const alreadyLinked =
    existingCategory && existingLabel?.id !== undefined
      ? isLabelLinkedCategory(existingCategory.id, existingLabel.id)
      : false;

  return {
    name: existingLabel?.name ?? labelName,
    id: existingLabel?.id,
    categoryId: existingCategory?.id,
    categoryName: existingCategory?.name ?? categoryName,
    needsLink: !alreadyLinked
  };
};

export const describeOption = (option: PendingLabel) => {
  if (option.alreadyAssigned)
    return `The label "${option.name}" is already assigned to this post`;
  if (option.isPending)
    return `The label "${option.name}" is pending addition.`;

  if (option.categoryName === undefined)
    return option.id === undefined
      ? `Create "${option.name}" (unassigned)`
      : option.name;

  const isNewCategory = option.categoryId === undefined;
  const isNewLabel = option.id === undefined;

  if (isNewCategory && isNewLabel)
    return `Create category "${option.categoryName}" with "${option.name}"`;
  if (isNewCategory)
    return `Add "${option.name}" to new category "${option.categoryName}"`;
  if (isNewLabel) return `Create "${option.name}" in "${option.categoryName}"`;
  if (option.needsLink)
    return `Link "${option.name}" to "${option.categoryName}"`;

  return option.name;
};

export const buildGroupedOption = (
  categoryDetails: LabelsCategories.Detail[],
  catalogLabels: PendingLabel[],
  isUnavailable: (id: number) => boolean
) => {
  const categoryBadges: Record<string, string> = {};
  const grouped: PendingLabel[] = [];

  categoryDetails.forEach((category) => {
    const available = category.labels.filter(
      (label) => !isUnavailable(label.id)
    );
    if (available.length === 0) return;

    categoryBadges[category.name] = formatBadge(
      available.length,
      category.labels.length
    );

    available
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((label) =>
        grouped.push({
          id: label.id,
          name: label.name,
          categoryId: category.id,
          categoryName: category.name
        })
      );
  });

  const categorizedLabelIds = categoryDetails.flatMap((c) =>
    c.labels.map((l) => l.id)
  );
  const uncategorized = catalogLabels
    .filter((label) => label.id !== undefined)
    .filter((label) => !categorizedLabelIds.includes(label.id as number))
    .filter((label) => !isUnavailable(label.id as number))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { groupedOptions: [...grouped, ...uncategorized], categoryBadges };
};

export const isNewCategory = (option: PendingLabel) =>
  option.categoryName !== undefined && option.categoryId === undefined;
